import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
  toast,
} from '@parcel/ui';
import { Bug, Loader2 } from 'lucide-react';
import * as React from 'react';
import { apiErrorCode } from '../../lib/api.js';
import {
  type FeedbackTargetType,
  type ReportContext,
  useReportDataIssue,
} from '../../lib/feedback.js';

interface ReportDataIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string | undefined;
  targetType: FeedbackTargetType;
  targetId: string;
  targetName: string;
  buildContext: () => ReportContext;
}

export function ReportDataIssueDialog({
  open,
  onOpenChange,
  workspaceId,
  targetType,
  targetId,
  targetName,
  buildContext,
}: ReportDataIssueDialogProps) {
  const [body, setBody] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const mutation = useReportDataIssue(workspaceId);
  const saving = mutation.isPending;

  // Reset on every open TRANSITION, not on every prop change, so a single dialog
  // instance reused across different targets (e.g. one dialog on many signal rows)
  // never carries a stale draft or error from the last entity it was opened for.
  // useLayoutEffect runs before paint, so a reopen can't briefly flash the last
  // session's body.
  const wasOpenRef = React.useRef(false);
  React.useLayoutEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (justOpened) {
      setBody('');
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    setError(null);
    try {
      await mutation.mutateAsync({
        targetType,
        targetId,
        body: body.trim(),
        context: buildContext(),
      });
      toast.success('Thanks. Our team will review this.');
      setBody('');
      onOpenChange(false);
    } catch (err) {
      const code = apiErrorCode(err);
      if (code === 'forbidden') {
        setError('Reporting data issues requires the Pro plan.');
      } else {
        setError('Could not submit your report. Please try again.');
      }
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Report a data issue</DialogTitle>
          <DialogDescription>{targetName}</DialogDescription>
        </DialogHeader>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          placeholder="Describe the issue you spotted. For example: the general contractor looks wrong."
          className={cn(
            'flex min-h-[96px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
            'ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
        />
        <p className="text-xs text-muted-foreground">
          Parcel includes the page you are viewing automatically, so you do not need to say where
          you are.
        </p>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving || body.trim().length === 0 || !workspaceId}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Submit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// The drawer action-slot button. Matches the shell's external-link button look
// (h-8 w-8, bordered) so it sits naturally beside it.
export function ReportIssueButton({ onClick }: { onClick: () => void }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            aria-label="Report a data issue"
            onClick={onClick}
          >
            <Bug className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Report a data issue</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
